/**
 * Agent audit-timeline rows for one ticket.
 *
 * Wraps GET /api/v1/agent/tickets/{n}/events. Returns the most recent
 * ticket_events for the ticket, newest first, joined with the actor user
 * so the frontend can render "Alice Acme" instead of a UUID. The backend
 * caps at 200; we ask for 20 by default to match the side-rail card
 * SPEC §9 calls for ("last 20 events").
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { AgentTicketEventResponse } from "@/types/api";

/** Cache key that varies per (ticket, limit, major_only). The default
 * timeline view (major_only=true) and the "Show All Events" expanded
 * view (major_only=false) keep separate cached payloads so toggling
 * back and forth is instant after the first fetch. */
export function ticketEventsQueryKey(
  ticketNumber: number,
  limit: number,
  majorOnly: boolean,
): readonly unknown[] {
  return [
    "agent",
    "tickets",
    "events",
    ticketNumber,
    limit,
    majorOnly,
  ] as const;
}

/** Stable partial key for invalidating all events caches for a ticket
 * regardless of (limit, majorOnly) - handy after a comment delete /
 * status change so both the default and "all events" views refresh. */
export function ticketEventsQueryKeyPrefix(
  ticketNumber: number,
): readonly unknown[] {
  return ["agent", "tickets", "events", ticketNumber] as const;
}

export function useTicketEvents(
  ticketNumber: number,
  options: { enabled?: boolean; limit?: number; majorOnly?: boolean } = {},
): UseQueryResult<AgentTicketEventResponse[]> {
  const limit = options.limit ?? 20;
  // Default the side-rail card to major events only - SPEC §9 calls
  // for an at-a-glance summary, not the full firehose. Callers that
  // need the full timeline (the "Show All Events" toggle) pass
  // ``majorOnly: false`` explicitly.
  const majorOnly = options.majorOnly ?? true;
  return useQuery({
    queryKey: ticketEventsQueryKey(ticketNumber, limit, majorOnly),
    enabled: options.enabled !== false,
    queryFn: async () => {
      const response = await api.get<AgentTicketEventResponse[]>(
        `/agent/tickets/${ticketNumber}/events`,
        { params: { limit, major_only: majorOnly } },
      );
      return response.data;
    },
  });
}
